/**
 * Thumbnail resolution for category list & publish (schedule) items when APIs expose `assets`.
 * Portrait EPG: prefer `assets['3:4'].program` then `assets['3:4'].episode`, then legacy fields.
 * When no orientation-specific image is available the resolver returns the sentinel
 * `CATEGORY_CONTENT_THUMBNAIL_PLACEHOLDER` ("NA"), which the IMAGE table column renders
 * as a "NA" label instead of a broken image.
 */

export const CATEGORY_CONTENT_THUMBNAIL_PLACEHOLDER = 'assets/images/default-image.png';

export function withThumbnailPlaceholder(url: string | undefined | null): string {
    const s = url != null ? String(url).trim() : '';
    return s.length > 0 ? s : CATEGORY_CONTENT_THUMBNAIL_PLACEHOLDER;
}

/** Portrait 3:4 artwork from EPG `assets` (program / episode). */
export function getEpgPortraitAssetUrl(item: any): string | undefined {
    return getEpgAssetUrlForRatio(item, '3:4');
}

/** Landscape 16:9 artwork from EPG `assets` (program / episode). */
export function getEpgLandscapeAssetUrl(item: any): string | undefined {
    return getEpgAssetUrlForRatio(item, '16:9');
}

/** Generic helper: pick `program` first, then `episode` from a given aspect-ratio bucket. */
function getEpgAssetUrlForRatio(item: any, ratioKey: string): string | undefined {
    const box = item?.assets?.[ratioKey];
    if (!box || typeof box !== 'object') {
        return undefined;
    }
    const program = box.program != null ? String(box.program).trim() : '';
    if (program.length > 0) {
        return program;
    }
    const episode = box.episode != null ? String(box.episode).trim() : '';
    if (episode.length > 0) {
        return episode;
    }
    return undefined;
}

/** CPP / metadata-style rows: landscape → banner, portrait → album, square → square, with fallbacks. */
export function resolveCppOrientationThumbnail(data: any, orientation: string | undefined | null): string {
    const o = (orientation ?? '').toString();
    if (o === 'Portrait') {
        return withThumbnailPlaceholder(
            data?.albumArt || data?.bannerArt || data?.squareArt || data?.image || data?.url
        );
    }
    if (o === 'Square') {
        return withThumbnailPlaceholder(
            data?.squareArt || data?.bannerArt || data?.albumArt || data?.image || data?.url
        );
    }
    return withThumbnailPlaceholder(data?.bannerArt || data?.albumArt || data?.squareArt || data?.image || data?.url);
}

/**
 * EPG schedule / list rows for tables.
 *
 * Episode / Program rows expose an `assets` block keyed by aspect ratio. For these rows
 * we only use the orientation-specific entry; if it's missing we return the placeholder
 * ("NA") rather than falling through to other image keys.
 *   Portrait  -> assets['3:4'].program -> assets['3:4'].episode
 *   Landscape -> assets['16:9'].program -> assets['16:9'].episode
 *
 * Channel rows have no `assets` block and expose the URL via `image` (with `bannerArt`,
 * `albumArt`, `imageURL` as legacy fallbacks). These keys are only consulted when the
 * row has no `assets` object.
 */
export function resolveEpgItemThumbnail(data: any, orientation: string | undefined | null): string {
    const o = (orientation ?? '').toString();
    const hasAssetsBlock = data?.assets && typeof data.assets === 'object';

    if (o === 'Portrait') {
        if (hasAssetsBlock) {
            return withThumbnailPlaceholder(getEpgPortraitAssetUrl(data));
        }
        return withThumbnailPlaceholder(data?.albumArt || data?.imageURL || data?.image);
    }
    if (hasAssetsBlock) {
        return withThumbnailPlaceholder(getEpgLandscapeAssetUrl(data));
    }
    return withThumbnailPlaceholder(data?.bannerArt || data?.image);
}

/** Publish-items search table row (mixed `source`). */
export function resolvePublishItemsRowThumbnail(data: any, orientation: string | undefined | null): string {
    const src = (data?.source ?? '').toString().toLowerCase();
    if (src === 'epg') {
        return resolveEpgItemThumbnail(data, orientation);
    }
    if (src === 'cpp') {
        return resolveCppOrientationThumbnail(data, orientation);
    }
    return withThumbnailPlaceholder(data?.bannerArt || data?.albumArt || data?.squareArt || data?.image || data?.url);
}
